import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import type { Kapitel } from '../types'
import { grannKapitel, kapitelIndex, laddaKapitel } from '../lib/content'
import { arbetsomraden, lektionsindex } from '../lib/lektionsplanering'
import { slugForBegrepp } from '../lib/begrepp'
import { hamtaForladdad } from '../lib/forladdat'
import { useSidTitel } from '../lib/seo'
import Flashcards from '../components/Flashcards'
import MarkeradText from '../components/MarkeradText'
import Avsnitt from '../components/Avsnitt'
import Quiz from '../components/Quiz'
import DelaKapitel from '../components/DelaKapitel'
import LyssnaKontroll from '../components/LyssnaKontroll'
import FeedbackRuta from '../components/FeedbackRuta'

type Flik = 'text' | 'begrepp' | 'instudering'

const FLIKAR: { id: Flik; rubrik: string }[] = [
  { id: 'text', rubrik: 'Text' },
  { id: 'begrepp', rubrik: 'Begrepp' },
  { id: 'instudering', rubrik: 'Instuderingsfrågor' },
]

export default function KapitelSida() {
  const { slug } = useParams<{ slug: string }>()
  const { hash } = useLocation()
  // Förrenderingen förladdar kapitlet så att första (och enda) server-rendern
  // har innehållet; i webbläsaren är cachen tom och effekten laddar som vanligt.
  const [kapitel, setKapitel] = useState<Kapitel | null>(() => hamtaForladdad(`kapitel:${slug}`))
  const [saknas, setSaknas] = useState(false)
  const [flik, setFlik] = useState<Flik>('text')
  const [begreppsvy, setBegreppsvy] = useState<'lista' | 'flashcards'>('lista')
  const [delar, setDelar] = useState(false)
  const brodtextRef = useRef<HTMLElement | null>(null)
  useSidTitel(kapitel ? `Kapitel ${kapitel.id}: ${kapitel.titel}` : undefined)

  useEffect(() => {
    let aktiv = true
    setKapitel(null)
    setSaknas(false)
    setFlik('text')
    setBegreppsvy('lista')
    setDelar(false)
    if (!slug) return
    laddaKapitel(slug).then((k) => {
      if (!aktiv) return
      if (k) setKapitel(k)
      else setSaknas(true)
    })
    return () => {
      aktiv = false
    }
  }, [slug])

  // Rulla till avsnittsankaret (teorisektionens Läs mer-länkar pekar på
  // /kapitel/<slug>#<ankare>) — först när kapitlet laddats och renderats,
  // annars finns målet inte i DOM ännu. decodeURIComponent: ankaret kan
  // innehålla å/ä/ö som webbläsaren procentkodar i location.hash.
  useEffect(() => {
    if (!kapitel || !hash) return
    const mal = document.getElementById(decodeURIComponent(hash.slice(1)))
    if (mal) mal.scrollIntoView()
  }, [kapitel, hash])

  if (saknas) {
    return (
      <div className="wrap">
        <div className="dokument">
          <h1>Kapitlet finns inte ännu</h1>
          <p>Det här kapitlet finns inte.</p>
          <Link className="knapp knapp-primar" to="/">
            Till kapitellistan
          </Link>
        </div>
      </div>
    )
  }

  if (!kapitel) return null

  const { forra, nasta } = grannKapitel(kapitel.id)
  // Indexets korta sammanfattning (samma text som <meta description>/llms.txt)
  // — visas nu synligt i stället för att bara ligga i osynlig metadata, till
  // nytta för både skummande elever och AI-verktyg som snabbt ska avgöra vad
  // sidan handlar om.
  const sammanfattning = kapitelIndex.find((k) => k.slug === kapitel.slug)?.beskrivning
  // Vilket lärarmaterial som täcker det här kapitlet — kopplade_kapitel finns
  // redan i data, det saknades bara en synlig länk hit från kapitelsidan.
  const relateradeAo = arbetsomraden.filter((a) => a.kopplade_kapitel.includes(kapitel.id))
  const relateradeLektioner = lektionsindex.filter((l) => l.kopplade_kapitel.includes(kapitel.id))

  return (
    <div className="wrap">
      <header className="kapitelhuvud">
        <div className="kapitelhuvud-rad no-print">
          <nav className="brodsmula" aria-label="Brödsmulor">
            <Link to="/">← Alla kapitel</Link>
          </nav>
          <div className="knapprad">
            <LyssnaKontroll malRef={brodtextRef} nyckel={kapitel.id} aktiv={flik === 'text'} />
            <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setDelar(true)}>
              ↗ Dela
            </button>
            <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => window.print()}>
              🖨 Skriv ut
            </button>
          </div>
        </div>
        <span className="kapitel-nr">Kapitel {kapitel.id}</span>
        <h1>{kapitel.titel}</h1>
        {sammanfattning && <p className="kapitel-sammanfattning">{sammanfattning}</p>}
        <div className="larandemal">
          <h2>Efter kapitlet ska du kunna</h2>
          <ul>
            {kapitel.larandemal.map((mal) => (
              <li key={mal}>{mal.replace(/\s*\(F[1-4](,\s*F[1-4])*\)\s*$/, '')}</li>
            ))}
          </ul>
        </div>
      </header>

      <div className="flikrad no-print" role="tablist" aria-label="Kapitlets delar">
        {FLIKAR.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            id={`flik-${f.id}`}
            aria-selected={flik === f.id}
            aria-controls={`panel-${f.id}`}
            className={flik === f.id ? 'aktiv' : ''}
            onClick={() => setFlik(f.id)}
          >
            {f.rubrik}
          </button>
        ))}
      </div>

      {/* Alla paneler ligger kvar i DOM (dolda med CSS) så att utskriften får med allt. */}
      <div
        id="panel-text"
        role="tabpanel"
        aria-labelledby="flik-text"
        className={`flik-panel${flik === 'text' ? '' : ' dold'}`}
      >
        <article className="brodtext" ref={brodtextRef}>
          <MarkeradText markdown={kapitel.intro} styckeKlass="ingress" />
          {kapitel.avsnitt.map((avsnitt) => (
            <Avsnitt key={avsnitt.rubrik} avsnitt={avsnitt} medAnkare />
          ))}
        </article>
      </div>

      <div
        id="panel-begrepp"
        role="tabpanel"
        aria-labelledby="flik-begrepp"
        className={`flik-panel print-nysida${flik === 'begrepp' ? '' : ' dold'}`}
      >
        <section className="sektion sektion-begrepp" aria-labelledby="begrepp-rubrik">
          <div className="sektion-huvud">
            <span className="sektion-ikon" aria-hidden="true">🔑</span>
            <h2 id="begrepp-rubrik">Begrepp</h2>
          </div>
          <p className="sektion-ingress">
            Kapitlets viktigaste begrepp. Läs dem som lista eller träna med flashcards — vänd
            kortet och se om du kan förklaringen.
          </p>
          <div className="vyvaxlare no-print" role="group" aria-label="Välj vy för begreppen">
            <button type="button" className={begreppsvy === 'lista' ? 'aktiv' : ''} onClick={() => setBegreppsvy('lista')}>
              Lista
            </button>
            <button
              type="button"
              className={begreppsvy === 'flashcards' ? 'aktiv' : ''}
              onClick={() => setBegreppsvy('flashcards')}
            >
              Flashcards
            </button>
          </div>
          {/* Listan ligger alltid i DOM så att utskriften får med den även i flashcard-läge.
              Varje begrepp länkar till sin egen sida (/begrepp/<slug>). */}
          <dl className={`begreppslista${begreppsvy === 'lista' ? '' : ' dold'}`}>
            {kapitel.begrepp.map((b) => {
              const slug = slugForBegrepp(b.begrepp)
              return (
                <div key={b.begrepp}>
                  <dt>{slug ? <Link to={`/begrepp/${slug}`}>{b.begrepp}</Link> : b.begrepp}</dt>
                  <dd>{b.forklaring}</dd>
                </div>
              )
            })}
          </dl>
          {begreppsvy === 'flashcards' && <Flashcards begrepp={kapitel.begrepp} />}
        </section>
      </div>

      <div
        id="panel-instudering"
        role="tabpanel"
        aria-labelledby="flik-instudering"
        className={`flik-panel print-nysida${flik === 'instudering' ? '' : ' dold'}`}
      >
        <section className="sektion sektion-instudering" aria-labelledby="instudering-rubrik">
          <div className="sektion-huvud">
            <span className="sektion-ikon" aria-hidden="true">💬</span>
            <h2 id="instudering-rubrik">Instuderingsfrågor</h2>
          </div>
          <p className="sektion-ingress">
            Frågor som hjälper dig att kontrollera att du förstått texten. Svara med egna ord —
            gärna skriftligt eller tillsammans med en klasskamrat.
          </p>
          <ol className="fragelista">
            {kapitel.instuderingsfragor.map((f) => (
              <li key={f.id}>{f.fraga}</li>
            ))}
          </ol>
        </section>
      </div>

      {/* Ligger under texten på skärm (syns bara på Text-fliken), och efter
          instuderingsfrågorna i utskriften. */}
      <section
        className={`sektion sektion-ovning print-nysida${flik === 'text' ? '' : ' dold'}`}
        aria-labelledby="ovning-rubrik"
      >
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">✏️</span>
          <h2 id="ovning-rubrik">Övningsuppgifter</h2>
        </div>
        <p className="sektion-ingress">
          Större uppgifter där du använder kapitlets begrepp och modeller. Nivån visar
          ungefärlig svårighetsgrad (E, C eller A). Gör uppgiften först — titta sedan i facit.
        </p>
        <div className="uppgiftslista">
          {kapitel.ovningsuppgifter.map((o) => (
            <div key={o.id} className="uppgiftskort">
              <div className="uppgiftskort-meta">
                <span className={`niva-badge niva-${o.niva}`} title={`Nivå ${o.niva}`}>
                  {o.niva}
                </span>
                {o.formaga.map((f) => (
                  <span key={f} className="formaga-chip">
                    {f}
                  </span>
                ))}
              </div>
              <p>{o.uppgift}</p>
              <details className="facit">
                <summary>Visa facit</summary>
                <p>{o.facit}</p>
              </details>
            </div>
          ))}
        </div>
      </section>

      <section
        className={`sektion sektion-quiz no-print${flik === 'text' ? '' : ' dold'}`}
        aria-labelledby="quiz-rubrik"
      >
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">🎯</span>
          <h2 id="quiz-rubrik">Quiz</h2>
        </div>
        <p className="sektion-ingress">
          Testa dig själv! Välj det alternativ du tror är rätt — du får en förklaring direkt
          efter varje fråga.
        </p>
        {kapitel.quizfragor.length > 0 && (
          <p className="pdf-lank">
            <a href={`/pdf/quiz-${kapitel.slug}.pdf`}>📄 Ladda ner quizet som PDF (utan facit)</a>
          </p>
        )}
        <Quiz key={kapitel.id} fragor={kapitel.quizfragor} />
      </section>

      {flik === 'text' && (relateradeAo.length > 0 || relateradeLektioner.length > 0) && (
        <section className="lararresurser no-print" aria-labelledby="lararresurser-rubrik">
          <h2 className="sektionsrubrik" id="lararresurser-rubrik">Lärarresurser för det här kapitlet</h2>
          <p className="sektionsingress">
            Lärarhandledning och lektioner i lärarmaterialet som täcker det här kapitlet.
          </p>
          <ul className="lararresurser-lista">
            {relateradeAo.map((a) => (
              <li key={a.id}>
                <Link to={`/lararhandledning/${a.id}`}>Arbetsområde {a.ordning}: {a.titel}</Link>
                <span className="lararresurser-etikett"> — lärarhandledning</span>
              </li>
            ))}
            {relateradeLektioner.map((l) => (
              <li key={l.lektion_id}>
                <Link to={`/lararhandledning/${l.arbetsomrade_id}/lektion/${l.lektion_id}`}>
                  Lektion {l.lektionsnummer}: {l.titel}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {flik === 'text' && <FeedbackRuta typ="kapitel" objektId={kapitel.id} benamning="det här kapitlet" />}

      {delar && <DelaKapitel titel={kapitel.titel} onStang={() => setDelar(false)} />}

      <nav className="kapitelnav no-print" aria-label="Kapitelnavigering">
        {forra && forra.slug && (
          <Link to={`/kapitel/${forra.slug}`}>
            <span>← Föregående kapitel</span>
            <strong>
              {forra.id}. {forra.titel}
            </strong>
          </Link>
        )}
        {nasta && nasta.slug && (
          <Link to={`/kapitel/${nasta.slug}`} className="nasta">
            <span>Nästa kapitel →</span>
            <strong>
              {nasta.id}. {nasta.titel}
            </strong>
          </Link>
        )}
      </nav>
    </div>
  )
}
